import {
  constFunction,
  constString,
  escapeDots,
  isConstNode,
  isOutputNode,
  list,
  type Node,
  opConcat,
  opMap,
  opPick,
  opRunHistory,
  opRunSummary,
  typedDict,
  varNode,
} from '@wandb/weave/core';

export interface WeaveConfig {
  panel2Config?: any;
  defaultWorkspaceState?: {
    panel2Config?: any;
    key: string;
    keyType: string;
  };
}

export function weavePanelForWeaveKey(
  key: string,
  keyType: string
): WeaveConfig {
  // var name does not matter, PanelExpression will auto-replace.
  // Using something unique & ugly to force out errors.
  const panel2Config: any = {};
  const vNode = varNode(list('run'), 'runs');
  if (keyType === 'wb_trace_tree') {
    panel2Config.exprAndPanelLocked = true;
    panel2Config.exp = opConcat({
      arr: opMap({
        arr: vNode,
        mapFn: constFunction({row: typedDict({})}, ({row}) => {
          return opRunHistory({
            run: row,
          });
        }),
      }),
    });
    panel2Config.panelId = 'wb_trace_tree-traceDebuggerFromRunHistory';
    panel2Config.panelConfig = {
      traceKey: key,
    };
  } else if (isTableFileType(keyType)) {
    // Default to summary view - users can toggle to stepper if needed
    panel2Config.exp = opPick({
      obj: opRunSummary({run: vNode}),
      key: constString(escapeDots(key)),
    });
  } else {
    const tableNode = opPick({
      obj: opRunSummary({run: vNode}),
      key: constString(escapeDots(key)),
    });
    panel2Config.exp = tableNode;
  }

  return {
    panel2Config,
    defaultWorkspaceState: {
      panel2Config,
      key,
      keyType,
    },
  };
  // Uncomment the below to auto-inject the table merge. For now, I
  // prefer not auto-injecting, but this may change as we get feedback.
  // const newExp = callOp(Panel2.panelIdToPanelOpName('merge'), {
  //   input: runTableNode,
  //   config: Op.constNodeUnsafe('any', {}),
  // });

  // return {
  //   panel2Config: {exp: newExp},
  // };
}

export type ViewMode = 'summary' | 'stepper';

const TABLE_FILE_TYPES = [
  'table-file',
  'incremental-table-file',
  'partitioned-table',
  'joined-table',
];

export function isTableFileType(keyType: string): boolean {
  return TABLE_FILE_TYPES.includes(keyType);
}

export function buildStepperConfig(key: string, keyType: string) {
  const vNode = varNode(list('run'), 'runs');
  return {
    exp: opRunHistory({run: vNode}),
    panelId: 'panel-stepper',
    panelConfig: {
      workingKeyAndType: {key, type: keyType},
      workingSliderKey: '_step',
      workingPanelId: 'merge.table',
    },
  };
}

export function buildSummaryConfig(key: string) {
  const vNode = varNode(list('run'), 'runs');
  const tableNode = opPick({
    obj: opRunSummary({run: vNode}),
    key: constString(escapeDots(key)),
  });
  return {
    exp: tableNode,
  };
}

/**
 * Check if an expression matches the structural pattern: runs.summary["<key>"]
 * This checks the expression tree structure only, not the result type.
 *
 * Expected structure:
 * OutputNode (fromOp.name: "pick")
 * ├─ inputs.obj: OutputNode (fromOp.name: "run-summary")
 * └─ inputs.key: ConstNode (string key)
 */
export function isRunSummaryPickExpression(
  node: Node | undefined | null
): boolean {
  if (!node) {
    return false;
  }
  if (!isOutputNode(node)) {
    return false;
  }
  if (node.fromOp.name !== 'pick') {
    return false;
  }

  const objInput = node.fromOp.inputs.obj;
  if (!isOutputNode(objInput) || objInput.fromOp.name !== 'run-summary') {
    return false;
  }

  const keyInput = node.fromOp.inputs.key;
  if (!isConstNode(keyInput)) {
    return false;
  }

  return true;
}

/**
 * Check if an expression is a run summary pick of a table type.
 *
 * @param node - The expression node to check
 * @param keyType - The keyType from defaultWorkspaceState (always used since
 *                  expression types are often unrefined)
 */
export function isRunSummaryTableExpression(
  node: Node | undefined | null,
  keyType?: string
): boolean {
  if (!isRunSummaryPickExpression(node)) {
    return false;
  }

  // Always use keyType from defaultWorkspaceState since expression types
  // are often unrefined ('any') in default workspace panels
  return keyType != null && isTableFileType(keyType);
}
